import { Fragment } from 'react';
import { Heading } from 'react-aria-components';
import { Trans, useTranslation } from 'react-i18next';
import { faVial } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Types
import { OrderDetailsProp } from '../types';

// Helpers
import { formatDate, isNumberConditions } from '../helpers';

// Styles
import styles from '../order-details.upp.module.scss';
import { DescriptionList, DescriptionPair } from './description-list';

type Props = {
  orderDetails: OrderDetailsProp;
};

export const About = ({ orderDetails }: Props) => {
  const { t, i18n } = useTranslation();
  const hasMultipleSubproducts = (orderDetails.patient?.tests?.length || 0) > 1;

  return (
    <section className={styles.sectionWrapper}>
      <div className={styles.aboutSectionWrapper}>
        <div className={styles.iconWrapper}>
          <FontAwesomeIcon icon={faVial} className={styles.icon} aria-hidden />
        </div>
        <div className={styles.aboutSectionContent}>
          <Heading level={2} className={styles.sectionHeading}>
            {t('orders.details.aboutThisTest')}
          </Heading>
          <div>
            <Heading
              level={3}
              className={
                hasMultipleSubproducts
                  ? styles.listHeadingBold
                  : styles.listHeading
              }
            >
              {t('orders.details.panelDetails')}
            </Heading>
          </div>
          {hasMultipleSubproducts ? (
            <div className={styles.panelSeparator} />
          ) : null}
          {(orderDetails.patient?.tests ?? []).map((test, testIndex) => (
            <Fragment
              key={`${test.diseasePanel}-${test.reportDate || testIndex}`}
            >
              {testIndex > 0 ? <div className={styles.panelSeparator} /> : null}
              <div
                data-testid={`order-detail-about-section-${test.type}`}
                className={styles.panelSectionWrapper}
              >
                <div>
                  <Heading level={4} className={styles.panelSectionHeading}>
                    {i18n.exists(`orders.details.product.${test.type}`) ? (
                      <Trans i18nKey={`orders.details.product.${test.type}`} />
                    ) : (
                      test.diseasePanel
                    )}
                  </Heading>
                  <DescriptionList>
                    <DescriptionPair
                      inline
                      lightDescription
                      term={t('orders.details.genesAnalyzed')}
                      description={
                        // If it's not a number, show the genes analyzed in italics
                        // Otherwise show the number of conditions or "N/A"
                        test.conditions &&
                        !isNumberConditions(test.conditions) ? (
                          <i>{test.conditions}</i>
                        ) : (
                          test.conditions ?? t('labels.na')!
                        )
                      }
                    />
                  </DescriptionList>
                </div>
                {i18n.exists(`orders.details.description.${test.type}`) ? (
                  <p className={styles.summary}>
                    <Trans
                      i18nKey={`orders.details.description.${test.type}`}
                    />
                  </p>
                ) : null}
                <div>
                  <Heading level={4} className={styles.panelSectionHeading}>
                    {t('orders.details.resultsReleased')}
                  </Heading>
                  <DescriptionList>
                    <DescriptionPair
                      term={t('orders.details.date')}
                      description={formatDate(test.reportDate)}
                    />
                  </DescriptionList>
                </div>
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};
